import { Attribute, Directive, ElementRef, Injector, Optional } from '@angular/core';

import { addClass, getClonedObject, initCaps, noop, runtimeEventManager, stringStartsWith } from '@wm/core';
import { IWidgetConfig, provideAsWidgetRef, register, propFactory as PropertiesFactory, StylableComponent, styler, WIDGET_CONSTANTS, Parent } from '@wm/components-base';
import { find, forEach, includes, isEmpty } from 'lodash-es';
import { Subject } from "rxjs";

enum ComponentType {
    PAGE,
    PREFAB,
    PARTIAL,
    WIDGET
}

interface CUSTOMWIDGET_CONFIG {
    widgetType: string;
    events: any;
    properties: any;
    customClasses: string;
}

const registeredPropsSet = new Set<string>();

const DEFAULT_CLS = 'app-html-container';
const WIDGET_CONFIG: IWidgetConfig = {
    widgetType: 'wm-custom-widget',
    hostClass: DEFAULT_CLS
};

const ALLFIELDS = 'All Fields';
const defaultOptions = 'Option 1, Option 2, Option 3';

let propertiesGroup: any,
    eventsGroup: any = {},
    widgetDefaultProps = {},
    customWidgetPropsMap: { [key: string]: any } = {},
    configProps: any = {},
    propsSkipList = ['width', 'height', 'show', 'animation'];

declare const _: any;

@Directive({
    selector: '[wmWidgetContainer]',
    standalone: true,
    providers: [
        provideAsWidgetRef(CustomWidgetContainerDirective)
    ],
    exportAs: 'wmWidgetContainer'
})
export class CustomWidgetContainerDirective extends StylableComponent {
    name: string;
    propsReady: Function;
    widgetName: string;
    private componentRefProvider: any;
    private propPanel: any;
    private props: any = {};
    config: any;
    configSubject: Subject<void>;
    base: any;

    constructor(
        inj: Injector, elRef: ElementRef,
        @Attribute('widgetname') widgetname: string,
        @Optional() viewParent: Parent
    ) {
        let resolveFn: Function = noop;
        WIDGET_CONFIG.widgetSubType = widgetname
        super(inj, WIDGET_CONFIG, viewParent, new Promise(res => resolveFn = res));
        this.propsReady = resolveFn;
        this.widgetName = widgetname;
        this.configSubject = new Subject();
        this.name = elRef.nativeElement.getAttribute('name');
        this.componentRefProvider = runtimeEventManager.getService('componentRefProvider');
        widgetDefaultProps = PropertiesFactory.getWidgetProperties('wm-custom-widget');
        this.propPanel = runtimeEventManager.getService('PropertyPanel');
        styler(this.nativeElement, this);
        this.setWidgetsPropertyGroups()
        // Call on property change on name to set name attribute on element.
        this.registerReadyStateListener(() => {
            super.onPropertyChange('name', this.name);
        });

        this.registerPropertyChangeListener((key: string, nv: any, ov?: any) => {
            if (key === 'dataset') {
                // update props panel to update dataset related props like orderby groupby
                runtimeEventManager.emit('updatePropPanelOptions', this);
            }
            let isFigmaProp = key.startsWith('prop-');
            let propName = isFigmaProp ? key.slice(5) : key;
            if(key === 'inputtype') {
                propName = 'type';
                (this as any)[propName] = nv;
                this.$element.attr('type', nv);
            }
            if (!key.startsWith('prop-')) {
                // find the base widget and set these props on the base widget on change for the props to reflect in canvas
                let customWidgetEl = this.$element.find('[wmcustomwidget]');
                if (this.base) {
                    let baseEl = customWidgetEl?.find('[name=' + this.base + ']')?.[0];
                    if (baseEl) {
                        baseEl.widget[propName] = nv;
                    }
                }
            }
            if (this.props.hasOwnProperty(propName) && this.props[propName] !== nv) {
                this.props[propName] = nv;
            }
        });
    }

    setWidgetsPropertyGroups() {
        let groups = this.propPanel.propertyGroups,
            basicPropsSubGrp;

        let propertyGroups = groups.filter((group: any) => {

            if (group.name === 'properties') {
                propertiesGroup = group;
            } else if (group.name === 'events') {
                eventsGroup = group;
            }

            return !!group.parent;
        });

        basicPropsSubGrp = find(propertiesGroup.subGroups, (subGroup) => {
            return !subGroup.name;
        });

        propsSkipList = propsSkipList.concat(basicPropsSubGrp.properties);
    }

    onConfigLoad(config: CUSTOMWIDGET_CONFIG) {
        let widgetProps: { [key: string]: any } = {},
            userDefinedProps,
            userDefinedEvents,
            userDefinedMethods,
            widgetEvents: Array<any> = [],
            widgetProperties: Array<any> = [];

        if (config.customClasses) {
            addClass(this.nativeElement, config.customClasses);
        }

        this.base = config.widgetType.split('wm-')[1];

        const isExists = propertiesGroup.subGroups.find((o: any) => { return o.name === this.widgetName + '_' + 'properties' });

        if (!isExists) {
            propertiesGroup.subGroups.splice(1, 0, {
                'boundWidgetName': this.widgetName,
                'name': this.widgetName + '_' + 'properties',
                'parent': 'properties',
                'properties': widgetProperties,
                'filteredProps': widgetProperties,
                'displayKey': 'LABEL_PROPERTYGROUP_CUSTOMWIDGETPROPS',
            }
            );

            eventsGroup.subGroups.push({
                'boundWidgetName': this.widgetName,
                'name': this.widgetName + '_' + 'events',
                'parent': 'events',
                'properties': widgetEvents
            });

            userDefinedProps = config.properties || {};
            userDefinedEvents = config.events || {};
            // userDefinedMethods = config.methods || {};

            // this.prefabid = config.id;

            forEach(userDefinedProps, (prop, key) => {
                widgetProps[`prop-${key}`] = prop;
                this.props[key] = prop.value;

                if (stringStartsWith(prop.value, 'bind:')) {
                    prop.__value = prop.value;
                    prop.value = undefined;
                }

                //   prop.value = this.overrideResourcePath(prop.value);

                if (!includes(propsSkipList, key)) {
                    widgetProperties.push(`prop-${key}`);
                }

                if (prop.hasOwnProperty('options') && stringStartsWith(prop.options, 'bind:')) {
                    prop.__options = prop.options;
                    delete prop.options;
                }

                if (prop.bindable === 'out-bound') {
                    prop.show = false;
                    prop.showAt = undefined;
                } else if (!prop.hasOwnProperty('show')) {
                    prop.show = true;
                } else if (stringStartsWith(prop.show, 'bind:')) {
                    prop.__show = prop.show;
                    prop.show = true;
                }

                if (!prop.hasOwnProperty('disabled')) {
                    prop.disabled = false;
                } else if (stringStartsWith(prop.disabled, 'bind:')) {
                    prop.__disabled = prop.disabled;
                    prop.disabled = false;
                }

                if (!prop.hasOwnProperty('bindable')) {
                    prop.bindable = true;
                }

                if (!prop.hasOwnProperty('bindInfo')) {
                    prop.bindInfo = { evaulateBindings: true, handleEmptyEvaluation: true };
                }

                if (prop.type === 'number' && prop.widget === 'list') {
                    prop.widget = 'list-number';
                }

                prop.widget = prop.type === 'date' ? 'date' : prop.widget;

                if (prop.value && prop.type === 'date') {
                    prop.value = new Date(prop.value);
                }

                prop.label = initCaps(key);
                prop.helpText = prop.description;

            });

            forEach(userDefinedEvents, (prop, key) => {
                widgetProps[key] = prop;

                widgetEvents.push(key);

                prop.displayName = prop.displayName ? 'On ' + prop.displayName : '';
                prop.show = true;
                prop.disabled = false;
                prop.type = 'event';
                prop.widget = 'eventlist';
                prop.options = WIDGET_CONSTANTS.EVENTS_OPTIONS;
                prop.helpText = prop.description;
            });
        }

        if (!isEmpty(this.props)) {
            configProps[this.widgetName] = this.props;
        }

        if (customWidgetPropsMap[this.widgetName]) {
            this.widgetProps = getClonedObject(customWidgetPropsMap[this.widgetName]);
            //   this._methodsMap = prefabMethodsMap[this.prefabName];
            runtimeEventManager.emit('wms:refresh-properties-panel');
            return;
        }

        forEach(widgetDefaultProps, (prop, key) => {
            if (widgetProps[key] === undefined) {
                widgetProps[key] = prop;
            }
        });

        customWidgetPropsMap[this.widgetName] = widgetProps;
        // prefabMethodsMap[this.widgetName]     = userDefinedMethods;

        this.widgetProps = getClonedObject(customWidgetPropsMap[this.widgetName]);
        // this._methodsMap = prefabMethodsMap[this.prefabName];
        registeredPropsSet.add(this.widgetName);
        runtimeEventManager.emit('widgetSelection');
    }

    override ngOnInit() {
        super.ngOnInit();
        this.compileContent()
    }

    compileContent() {
        let widgetEle = '<div class="full-width full-height"></div>',
            $widgetEle = $(widgetEle)

        this.$element.append($widgetEle);
        this.componentRefProvider.getComponentFactoryRef(this.widgetName, ComponentType.WIDGET, { widgetRef: this }).then((compRef: any) => {
            let compiledEl = compRef.location.nativeElement;
            this.$element.find('.full-width.full-height').html('');
            this.$element.find('.full-width.full-height').append($(compiledEl));
        });
    }

    public setProps(config: any, resolveFn: Function) {
        this.config = config;
        this.configSubject.next();
        if (!config || !config.properties) {
            register(this.widgetName, widgetDefaultProps)
            this.propsReady(resolveFn);
            return;
        }
        if (!registeredPropsSet.has(this.widgetName)) {
            register(this.widgetName, this.prepareProps(config));
        } else {
            this.prepareProps(config);
            this.props = Object.assign(this.props, getClonedObject(configProps[this.widgetName]));
            this.onConfigLoad(config);
        }

        this.propsReady(resolveFn);
    }

    private prepareProps(config: CUSTOMWIDGET_CONFIG) {
        this.onConfigLoad(config);
        return this.widgetProps;
    }
}
